import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Socket } from 'socket.io-client';
import { useAuth } from '@modules/iam';
import { createNotificationSocket } from '../infrastructure/notificationSocket';

interface AdminRealtimeContextValue {
  socket: Socket | null;
  connected: boolean;
}

const AdminRealtimeContext = createContext<AdminRealtimeContextValue>({
  socket: null,
  connected: false,
});

export function AdminRealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    const nextSocket = createNotificationSocket(token, {
      onConnect: () => setConnected(true),
      onDisconnect: () => setConnected(false),
    });

    socketRef.current = nextSocket;
    void Promise.resolve().then(() => setSocket(nextSocket));

    return () => {
      nextSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
    };
  }, [user]);

  return (
    <AdminRealtimeContext.Provider value={{ socket, connected }}>
      {children}
    </AdminRealtimeContext.Provider>
  );
}

export function useAdminRealtime(): AdminRealtimeContextValue {
  return useContext(AdminRealtimeContext);
}